import { api } from '@web/api/client'
import type {
  AppDefinition,
  BridgeConfig,
  SessionHistoryItem,
  SessionRecord,
  SessionStatus,
} from '@web/api/types'
import { groupedApps } from '@web/components/AppGallery'
import { CanopyMark } from '@web/components/CanopyMark'
import { IdentitySwitcher } from '@web/components/Identity'
import { RenameSession } from '@web/components/RenameSession'
import { DesktopSessionIdentitySlot } from '@web/components/SessionHeading'
import { Icon } from '@web/components/ui/icons'
import { appIcon } from '@web/lib/appIcon'
import { basename, fmtRelative, modelLabel } from '@web/lib/format'
import { useIdentity } from '@web/state/identity'
import { ACTIVE_STATUSES, groupSessions } from '@web/state/session-order'
import { dropSessionStore } from '@web/state/session-store'
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'

const STATUS_COLOR: Record<SessionStatus, string> = {
  created: 'bg-faint',
  starting: 'bg-warn pulse',
  ready: 'bg-ok',
  running: 'bg-accent pulse',
  requires_action: 'bg-warn pulse',
  idle: 'bg-ok',
  exited: 'bg-faint',
  error: 'bg-danger',
  detached: 'bg-warn',
  archived: 'bg-faint',
  closed: 'bg-faint',
  asleep: 'bg-faint',
}
/** Fetch a historical release icon only when its row approaches the visible history. */
function HistoryIcon({ src, title }: { src: string; title: string }) {
  const element = useRef<HTMLSpanElement>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const node = element.current
    if (!node || visible) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { root: node.closest('.sidebar-history'), rootMargin: '96px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [visible])
  return (
    <span ref={element} className="app-history-icon-placeholder">
      {visible ? (
        <img
          className="app-history-icon"
          src={appIcon(src, 64)}
          alt={`${title} app`}
          width={32}
          height={32}
          decoding="async"
          loading="lazy"
        />
      ) : (
        <span aria-hidden="true" className="app-history-icon-placeholder-mark">
          <Icon name="gauge" size={16} />
        </span>
      )}
    </span>
  )
}

export function Sidebar({
  app,
  apps,
  sessions,
  selectedId,
  onSelect,
  onNew,
  refreshing,
  config,
  configError,
  onHome,
  busy,
  onApp,
  onRenamed,
  collapsed,
  archived,
  onToggleArchived,
  onChanged,
  hasMoreSessions,
  loadingMoreSessions,
  onLoadMoreSessions,
}: {
  collapsed: boolean
  archived: boolean
  onToggleArchived: () => void
  onChanged: () => Promise<void>
  hasMoreSessions: boolean
  loadingMoreSessions: boolean
  onLoadMoreSessions: () => Promise<void>
  app?: AppDefinition
  apps: AppDefinition[]
  sessions: SessionHistoryItem[]
  selectedId: string | null
  onSelect: (id: string) => void
  onNew: () => void
  refreshing: boolean
  config: BridgeConfig | null
  configError: string | null
  onHome: () => void
  busy?: boolean
  onApp: (id: string | null) => void
  onRenamed: (record: SessionRecord) => void
}) {
  const [actionPending, setActionPending] = useState<Set<string>>(new Set())
  const [hiddenArchives, setHiddenArchives] = useState<Set<string>>(new Set())
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState('')
  const run = async (session: SessionHistoryItem, archive: boolean) => {
    setMenu(null)
    setFailure('')
    setActionErrors((errors) => {
      const next = { ...errors }
      delete next[session.id]
      return next
    })
    setActionPending((pending) => new Set(pending).add(session.id))
    if (archive) setHiddenArchives((hidden) => new Set(hidden).add(session.id))
    try {
      if (archive) {
        await api.archiveSession(session.id)
        dropSessionStore(session.id)
      } else await api.stopSession(session.id)
      if (archive && selectedId === session.id) onNew()
    } catch (error) {
      const message = (error as Error).message
      setFailure(message)
      setActionErrors((errors) => ({ ...errors, [session.id]: message }))
      if (archive)
        setHiddenArchives((hidden) => {
          const next = new Set(hidden)
          next.delete(session.id)
          return next
        })
    } finally {
      setActionPending((pending) => {
        const next = new Set(pending)
        next.delete(session.id)
        return next
      })
    }
    // A failed refresh must not undo a successfully committed archive.
    await onChanged().catch((error: Error) => setFailure(error.message))
  }
  const [picker, setPicker] = useState(false)
  const [menu, setMenu] = useState<string | null>(null)
  const identity = useIdentity()
  const [renaming, setRenaming] = useState<SessionHistoryItem | null>(null)
  const root = useRef<HTMLElement>(null)
  const pickerTrigger = useRef<HTMLButtonElement>(null)
  const menuTrigger = useRef<HTMLButtonElement | null>(null)
  const pickerPanel = useRef<HTMLDivElement>(null)
  const menuPanel = useRef<HTMLDivElement>(null)
  const focusChoices = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      ![
        'ArrowDown',
        'ArrowUp',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
      ].includes(event.key)
    )
      return
    const choices = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        'button:not(:disabled)',
      ),
    )
    const index = choices.indexOf(document.activeElement as HTMLButtonElement)
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? choices.length - 1
          : (index +
              (['ArrowUp', 'ArrowLeft'].includes(event.key) ? -1 : 1) +
              choices.length) %
            choices.length
    event.preventDefault()
    choices[next]?.focus()
  }
  useEffect(() => {
    if (picker)
      (
        pickerPanel.current?.querySelector<HTMLButtonElement>(
          '[aria-current="page"]',
        ) ?? pickerPanel.current?.querySelector<HTMLButtonElement>('button')
      )?.focus()
    if (menu)
      menuPanel.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }, [picker, menu])
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setPicker(false)
        setMenu(null)
      }
    }
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape' && (picker || menu)) {
        event.preventDefault()
        ;(picker ? pickerTrigger.current : menuTrigger.current)?.focus()
        setPicker(false)
        setMenu(null)
      }
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onEscape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onEscape)
    }
  }, [picker, menu])
  const groups = useMemo(
    () =>
      groupSessions(
        sessions.filter(
          (session) =>
            (session.status === 'archived') === archived &&
            (archived || !hiddenArchives.has(session.id)),
        ),
        app?.id,
      ),
    [sessions, app?.id, archived, hiddenArchives],
  )
  const appGroups = useMemo(() => groupedApps(apps), [apps])
  const syncLabel = configError
    ? 'Session updates unavailable. Retrying automatically every 15 seconds.'
    : refreshing
      ? 'Updating sessions…'
      : 'Sessions update automatically every 15 seconds.'

  return (
    <aside
      ref={root}
      aria-label="Workspace navigation"
      className={`workspace-sidebar chrome relative flex shrink-0 flex-col bg-chrome text-chrome-fg ${collapsed ? 'is-collapsed' : 'is-expanded'}`}
    >
      <div className="sidebar-heading flex items-center gap-3 px-4 pb-3 pt-4">
        <button
          ref={pickerTrigger}
          aria-haspopup="dialog"
          aria-label="Switch agent"
          aria-expanded={picker}
          onClick={() => {
            setPicker(!picker)
            setMenu(null)
          }}
          className="relative shrink-0"
        >
          {app ? (
            <img
              src={appIcon(app.iconUrl, 128)}
              width={44}
              height={44}
              decoding="async"
              alt=""
              className="h-11 w-11 rounded-xl"
            />
          ) : (
            <CanopyMark size={44} />
          )}
          <span className="absolute -bottom-1 -right-1 rounded-full bg-chrome">
            <Icon name="chevronDown" size={12} />
          </span>
        </button>
        {picker && (
          <div
            ref={pickerPanel}
            className="app-picker"
            role="dialog"
            aria-label="Switch agent"
            onKeyDown={focusChoices}
            onBlur={(event) => {
              if (
                !event.currentTarget.contains(event.relatedTarget) &&
                event.relatedTarget !== pickerTrigger.current
              )
                setPicker(false)
            }}
          >
            <div className="app-picker-grid app-picker-home-row">
              <button
                aria-current={!app ? 'page' : undefined}
                onClick={() => {
                  setPicker(false)
                  pickerTrigger.current?.focus()
                  onApp(null)
                }}
              >
                <CanopyMark size={48} />
                <span className="app-picker-title">All Agents</span>
              </button>
            </div>
            {appGroups.map(({ name, items }) => (
              <section
                className="app-picker-group"
                aria-label={name}
                key={name}
              >
                <h2>{name}</h2>
                <div className="app-picker-grid">
                  {items.map((item) => (
                    <button
                      key={`${item.id}:${item.digest}`}
                      title={item.title}
                      aria-current={item.id === app?.id ? 'page' : undefined}
                      onClick={() => {
                        setPicker(false)
                        pickerTrigger.current?.focus()
                        onApp(item.id)
                      }}
                    >
                      <img
                        src={appIcon(item.iconUrl, 128)}
                        width={48}
                        height={48}
                        decoding="async"
                        alt=""
                      />
                      <span className="app-picker-title">{item.title}</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
        <div className="sidebar-heading-copy min-w-0 flex-1">
          <div className="truncate text-[15px] font-semibold">
            {app?.title ?? 'Canopy'}
          </div>
          <div className="sidebar-workspace-caption truncate text-[11.5px] text-chrome-muted">
            {configError ? 'Connection unavailable' : 'Your personal workspace'}
          </div>
          <DesktopSessionIdentitySlot />
        </div>
        <span
          className={`sidebar-sync${refreshing ? ' is-refreshing' : ''}${configError ? ' has-error' : ''}`}
          role="img"
          aria-label={syncLabel}
          title={syncLabel}
        />
      </div>
      {app &&
        app.launchMode !== 'overview' &&
        app.launchMode !== 'direct' &&
        app.descriptor?.configuration?.sessions.nature !== 'subagent' && (
          <div className="sidebar-new px-3 pb-3">
            <button
              className="btn btn-primary h-10 w-full justify-center rounded-control text-[14px]"
              onClick={onNew}
              disabled={busy}
            >
              {busy
                ? 'Opening…'
                : app.descriptor?.configuration?.sessions.nature === 'implicit'
                  ? 'Open workspace'
                  : 'New Session'}
            </button>
          </div>
        )}
      <div className="sidebar-history min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        {failure && (
          <p role="alert" className="px-2 py-2 text-xs text-danger">
            {failure}
          </p>
        )}
        {groups.length === 0 && (
          <div className="px-2 py-6 text-center text-xs text-chrome-muted">
            {archived ? 'No archived sessions.' : 'No sessions here yet.'}
            <span className="block mt-1">Choose an agent to begin.</span>
          </div>
        )}
        {groups.map(({ key, section, project, items, showProject }) => (
          <div key={key} className="mb-2">
            {section && (
              <h2 className="sidebar-group-label">
                {archived ? 'Archived sessions' : section}
              </h2>
            )}
            {showProject && (
              <div
                className="truncate px-2 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-chrome-muted/70"
                title={project}
              >
                {basename(project) || project}
              </div>
            )}
            {items.map((s) => (
              <div key={s.id} className="session-history-row">
                <button
                  onClick={() => onSelect(s.id)}
                  aria-current={s.id === selectedId ? 'page' : undefined}
                  className={`flex min-w-0 flex-1 items-start gap-2.5 rounded-control px-2.5 py-2 text-left transition-colors hover:bg-chrome-hover ${
                    s.id === selectedId ? 'bg-chrome-active' : ''
                  }`}
                >
                  <span className="relative shrink-0">
                    <HistoryIcon
                      src={
                        s.config.package_digest
                          ? `/v1/apps/${s.app_id}/icon?packageDigest=${encodeURIComponent(s.config.package_digest)}`
                          : (apps.find((item) => item.id === s.app_id)
                              ?.iconUrl ??
                            `/v1/apps/${encodeURIComponent(s.app_id)}/icon`)
                      }
                      title={
                        apps.find((item) => item.id === s.app_id)?.title ??
                        s.app_id
                      }
                    />
                    <span
                      title={actionErrors[s.id] ?? s.status}
                      className={`absolute -top-1 -right-1 inline-block h-3 w-3 rounded-full border-2 border-chrome ${actionErrors[s.id] ? 'bg-danger' : (STATUS_COLOR[s.status] ?? 'bg-faint')}`}
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block truncate text-[13.5px] ${s.title_provisional ? 'provisional-title' : ''}`}
                    >
                      {s.title}
                      {s.owner_user_id !== identity.state?.current_user?.id &&
                        ' · Shared'}
                    </span>
                    <span className="block truncate text-[11px] text-chrome-muted">
                      {s.model || s.config.model
                        ? modelLabel(
                            s.model ?? s.config.model,
                            config?.models ?? [],
                          )
                        : (apps.find((item) => item.id === s.app_id)?.title ??
                          'Session')}{' '}
                      · {fmtRelative(s.updated_at)}
                    </span>
                    {actionErrors[s.id] && (
                      <span
                        role="status"
                        className="block text-[11px] text-danger"
                        title={actionErrors[s.id]}
                      >
                        Action failed. Try again.
                      </span>
                    )}
                  </span>
                </button>
                <button
                  aria-label={`Session options: ${s.title}`}
                  hidden={s.owner_user_id !== identity.state?.current_user?.id}
                  disabled={actionPending.has(s.id)}
                  aria-expanded={menu === s.id}
                  className="session-options mt-2 shrink-0 p-1 text-chrome-muted"
                  aria-haspopup="menu"
                  onClick={(event) => {
                    menuTrigger.current = event.currentTarget
                    setMenu(menu === s.id ? null : s.id)
                    setPicker(false)
                  }}
                >
                  <span aria-hidden="true">☰</span>
                </button>
                {menu === s.id && (
                  <div
                    ref={menuPanel}
                    className="session-menu"
                    role="menu"
                    aria-label="Session options"
                    onKeyDown={focusChoices}
                    onBlur={(event) => {
                      if (
                        !event.currentTarget.contains(event.relatedTarget) &&
                        event.relatedTarget !== menuTrigger.current
                      )
                        setMenu(null)
                    }}
                  >
                    <button
                      role="menuitem"
                      onClick={() => {
                        setRenaming(s)
                        setMenu(null)
                      }}
                    >
                      Rename session
                    </button>
                    {ACTIVE_STATUSES.has(s.status) &&
                      s.status !== 'created' && (
                        <button
                          role="menuitem"
                          onClick={() => void run(s, false)}
                        >
                          Stop session
                        </button>
                      )}
                    {s.status !== 'archived' && (
                      <button role="menuitem" onClick={() => void run(s, true)}>
                        Archive session
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
        {hasMoreSessions && (
          <button
            type="button"
            className="w-full px-3 py-2 text-left text-xs text-chrome-muted hover:underline disabled:opacity-50"
            onClick={() => void onLoadMoreSessions()}
            disabled={loadingMoreSessions}
          >
            {loadingMoreSessions
              ? 'Loading older sessions…'
              : 'Load older sessions'}
          </button>
        )}
      </div>
      <button
        type="button"
        className="sidebar-archive-link px-4 pb-3 text-left text-[11px] text-chrome-muted hover:underline"
        onClick={onToggleArchived}
        aria-pressed={archived}
      >
        {archived ? 'Back to sessions' : 'Archived sessions'}
      </button>
      <IdentitySwitcher compact={collapsed} />
      {renaming && (
        <RenameSession
          session={renaming}
          returnFocus={menuTrigger.current}
          onClose={() => setRenaming(null)}
          onSaved={onRenamed}
        />
      )}
    </aside>
  )
}
